<template>
  <div class="filter-container">
    <Button
      :label="$t('products.filters.clearAllFilters')"
      :type="isFilterEnabled ? 'btn-default' : 'btn-disabled'"
      size="is-fullwidth"
      class="clear-all-btn"
      @click="handleFilterReset"
    />
    <FilterCard
      v-if="brands.length > 0"
      :title="$t('products.brands')"
      class="brand-filters-container"
    >
      <template #search-box>
        <TextField
          v-model="searchBrand"
          :field-name="$t('products.searchBrands')"
          icon="search"
          :placeholder="$t('products.searchBrands')"
        />
      </template>
      <Checkbox
        v-for="(brand, index) in filteredBrandList"
        :id="brand.slugUrl"
        :key="brand.slugUrl"
        v-model="brandFiltersParamsModel"
        :native-value="brand.slugUrl"
        :is-small="true"
        @input="
          trackFilterEvent($event, {
            value: brand.name,
            slugUrl: brand.slugUrl,
            filterIndex: index,
            filterCategoryIndex: 0,
            type: 'brand'
          })
        "
      >
        {{ brand.name }}
      </Checkbox>
    </FilterCard>

    <FilterCard
      :title="$t('dictionary.price')"
      class="price-range-filter-container"
    >
      <PriceRangeFilterContainer
        :min-range="priceRange.minPrice / 100"
        :max-range="priceRange.maxPrice / 100"
        :is-reset="isResetPriceSlider"
        @resetted="isResetPriceSlider = false"
      />
    </FilterCard>

    <FilterCard
      v-for="filterType in filterTypes"
      :key="filterType.name"
      :title="filterType.name"
      class="filter-type-filters-container"
    >
      <Checkbox
        v-for="(filterValue, index) in filterType.filterValues"
        :id="`filterType${filterValue.id}`"
        :key="filterValue.name"
        :value="storeFilterParams.type[filterType.slugUrl]"
        :native-value="filterValue.slugUrl"
        :is-small="true"
        @input="
          setStoreTypeFilters($event, filterType.slugUrl)
          trackFilterEvent($event, {
            value: filterValue.slugUrl,
            slugUrl: filterValue.slugUrl,
            filterIndex: index,
            filterCategoryIndex: 1,
            type: filterType.slugUrl
          })
        "
      >
        {{ filterValue.name }}
      </Checkbox>
    </FilterCard>

    <FilterCard
      v-if="otherFilters.length > 0"
      class="other-filters-container"
      :title="$t('products.others')"
    >
      <Checkbox
        v-for="(other, index) in otherFilters"
        :id="`other${other.slugUrl}`"
        :key="other.slugUrl"
        v-model="otherFiltersParamsModel"
        :native-value="other.slugUrl"
        :is-small="true"
        @input="
          trackFilterEvent($event, {
            value: other.slugUrl,
            slugUrl: other.slugUrl,
            filterIndex: index,
            filterCategoryIndex: 2,
            type: 'other'
          })
        "
      >
        {{ $t(`products.filters.${other.name}`) }}
      </Checkbox>
    </FilterCard>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Button from '@sephora-asia/core-button/Button.vue'
import Checkbox from '@sephora-asia/inputs-checkbox/Checkbox.vue'
import TextField from '@sephora-asia/inputs-text-field/TextField.vue'
import isEmpty from 'lodash/isEmpty'
import filterTracking from '../mixins/filterTracking'
import i18n from '../mixins/i18n'
import queryString from '../mixins/queryString'
import productFiltersHelper from '../mixins/productFiltersHelper'
import FilterCard from './FilterCard.vue'
import PriceRangeFilterContainer from './PriceRangeFilterContainer.vue'

export default {
  name: 'FilterCardsContainer',
  components: {
    Button,
    Checkbox,
    FilterCard,
    TextField,
    PriceRangeFilterContainer
  },
  mixins: [filterTracking, i18n, queryString, productFiltersHelper],
  props: {
    brands: {
      type: Array,
      required: true
    },
    filterTypes: {
      type: Array,
      required: true
    },
    otherFilters: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      searchBrand: '',
      isResetPriceSlider: false,
      // For the watcher to reset price query before pushing route
      isResetPriceQuery: false
    }
  },
  computed: {
    ...mapGetters({
      storeFilterParams: 'listingPageFilters/filterParams',
      priceRange: 'listingPageFilters/priceRangeFilter'
    }),

    isFilterEnabled() {
      const query = this.filterQueryParams(
        'sort',
        'page',
        'view',
        'language',
        'q'
      )

      if (this.storeFilterParams.brands.length > 0 || this.searchBrand) {
        return true
      }

      return !isEmpty(query)
    },

    filteredBrandList() {
      if (this.searchBrand) {
        return this.brands.filter((brand) => {
          // To search for accented brands name. For eg:- Chloé
          // Ref: https://github.com/shentao/vue-multiselect/issues/984
          return this.normalizeAccentedProductName(brand.name).includes(
            this.normalizeAccentedProductName(this.searchBrand).trim()
          )
        })
      }
      return this.brands
    },

    brandFiltersParamsModel: {
      get() {
        return this.storeFilterParams.brands
      },
      set(value) {
        this.$store.commit(
          'listingPageFilters/setListingPageBrandFilterParams',
          value
        )
      }
    },
    otherFiltersParamsModel: {
      get() {
        return this.storeFilterParams.others
      },
      set(value) {
        this.$store.commit(
          'listingPageFilters/setListingPageOtherFilterParams',
          value
        )
      }
    }
  },
  watch: {
    storeFilterParams: {
      handler() {
        const selectedFiltersQuery = {
          page: undefined,
          ...this.buildSelectedFiltersQuery(this.storeFilterParams)
        }

        if (this.isResetPriceQuery) {
          Object.assign(selectedFiltersQuery, {
            'min-price': null,
            'max-price': null
          })
        }

        this.sortAndBuildUrlQuery(selectedFiltersQuery)
      },
      deep: true
    }
  },
  methods: {
    handleFilterReset(event) {
      this.searchBrand = ''
      this.isResetPriceSlider = true
      this.isResetPriceQuery = true
      this.trackClearFilterEvent()
      this.$store.commit('listingPageFilters/clearListingPageFilterParams')
    },
    setStoreTypeFilters(event, filterKey) {
      this.$store.commit('listingPageFilters/setListingPageTypeFilterParams', {
        [filterKey]: event
      })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .clear-all-btn {
  margin-top: 12px;
  padding: 9px 10px;
}

.price-range-filter-container {
  ::v-deep {
    .filter-card-content {
      padding-bottom: 5px;
      padding-top: 25px;
      .slider {
        margin: auto;
      }
    }
  }
}
</style>
